import { useMutation } from '@apollo/client/react';
import {
  CallNextcustomerDocument,
  type CompleteStylistFragment,
  EndBreakDocument,
  EndServiceDocument,
  StartServiceDocument,
} from '@drycut/graphql';
import { Button } from '@drycut/ui-components';
import { useBoolean } from 'usehooks-ts';

import { useStylistCardContext } from '../StylistCardContext';
import ManualPaymentReminder from './ManualPaymentReminder';

type Props = {
  stylist: CompleteStylistFragment;
};

export default function StartButton({ stylist }: Props): React.ReactElement {
  const { toggleActions, setActionLoading, actionLoading } = useStylistCardContext();
  const { value: manualPayModalOpen, toggle: toggleManualPayModal } = useBoolean(false);
  const { currentServiceExecution } = stylist;
  const manualBooking = currentServiceExecution?.booking?.manual || false;

  const [endService, { loading: ending }] = useMutation(EndServiceDocument, {
    variables: { input: { serviceExecutionId: currentServiceExecution?.id || '' } },
  });
  const [startService, { loading: starting }] = useMutation(StartServiceDocument, {
    variables: { input: { serviceExecutionId: currentServiceExecution?.id || '' } },
  });
  const [endBreak, { loading: breaking }] = useMutation(EndBreakDocument, {
    variables: { input: { stylistId: stylist.id } },
  });
  const [callNextCustomer, { loading: calling }] = useMutation(CallNextcustomerDocument, {
    variables: { input: { stylistId: stylist.id } },
  });

  const loading = starting || ending || breaking || calling;

  function handleReminderClose() {
    toggleManualPayModal();
    nextCustomerFlow();
  }

  // Sequence of action to be executed when the stylist clicks on "Next customer".
  async function nextCustomerFlow() {
    await endService();
    await callNextCustomer();
    toggleActions();
  }

  async function handleStartOrNextCustomer(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setActionLoading(true);

    switch (stylist.state) {
      case 'BREAK': {
        await endBreak();
        await callNextCustomer();
        toggleActions();
        break;
      }
      case 'WAITING_CUSTOMER': {
        await startService();
        toggleActions();
        break;
      }
      case 'WORKING': {
        if (manualBooking) {
          toggleManualPayModal();
        } else {
          nextCustomerFlow();
        }
        break;
      }
      default:
        // IDLE state doesn't allow to click the Next client/Start service button.
        break;
    }
    setActionLoading(false);
  }

  return (
    <>
      <Button
        color="primary"
        disabled={stylist.state === 'IDLE' || actionLoading}
        onClick={handleStartOrNextCustomer}
        loading={loading}
        className="text-base"
      >
        {stylist.state === 'WAITING_CUSTOMER' ? 'Start' : 'Nächster Kunde'}
      </Button>

      <ManualPaymentReminder open={manualPayModalOpen} onClose={handleReminderClose} />
    </>
  );
}
